import { Alert, Grid, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { AutogiroMandateStatusEnum } from 'src/shared/models/enums/autogiroMandateStatus.enum';
import { getAutogiroReminderTemplate, sendAutogiroReminder } from 'src/shared/services/autogiroMandateService';
import { AutogiroMandateResponse } from '../models/autogiroMandateResponse';

// Platshållarna som texten kan innehålla. Belopp och organisation fylls bara i för givare, för
// en medlem blir de tomma, därför står det i informationsrutan.
const reminderCustomFields = [
  { label: 'Namn', value: '[name]' },
  { label: 'Signeringslänk', value: '[signingLink]' },
  { label: 'Belopp', value: '[amount]' },
  { label: 'Organisation', value: '[organization]' },
];

type AutogiroReminderModalProps = {
  modalOpen: boolean;
  mandates: AutogiroMandateResponse[];
  onClose: () => void;
  onSent: () => void;
};

type ReminderForm = {
  subject: string;
  body: string;
};

/**
 * Manuell påminnelse om att signera autogiromedgivandet.
 *
 * Standardtexten är medvetet neutral, eftersom ett medgivande kan höra till en medlem eller till
 * en givare och urvalet kan innehålla båda. Autojobbens texter är skrivna för var sin målgrupp och
 * återanvänds därför inte här.
 */
export const AutogiroReminderModal = (props: AutogiroReminderModalProps) => {
  const { modalOpen, mandates, onClose, onSent } = props;
  const { t } = useTranslation(['invoice', 'common']);
  const isMounted = useIsMounted();

  const [isSending, setIsSending] = useState<boolean>(false);

  // Bara nya medgivanden kan påminnas, och mottagaren måste gå att nå. Servern gör samma
  // kontroll, det här är för att handläggaren ska se det innan utskicket.
  const isNew = (x: AutogiroMandateResponse) => x.status === AutogiroMandateStatusEnum.New;
  const hasEmail = (x: AutogiroMandateResponse) => !!x.linkedEmail && x.linkedEmail.indexOf('@') > 0;

  const eligible = mandates.filter((x) => isNew(x) && hasEmail(x));
  const wrongStatus = mandates.filter((x) => !isNew(x));
  const missingEmail = mandates.filter((x) => isNew(x) && !hasEmail(x));

  const formMethods = useForm<ReminderForm>({
    defaultValues: {
      subject: t('invoice:autogiroReminder.defaultSubject'),
      body: '',
    },
  });

  const { getValues, reset } = formMethods;

  // Brödtexten är en komplett HTML-mall med kundens logga och kontaktuppgifter, samma som
  // insamlingens påminnelser använder. Den byggs på servern och hämtas när modalen öppnas.
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const res = await getAutogiroReminderTemplate();

        if (isMounted() && res.data) reset({ subject: res.data.subject, body: res.data.body });
      } catch (e) {
        ConsoleHelper.log(e);
      }
    };

    fetchTemplate();
  }, [isMounted, reset]);

  const send = async () => {
    if (eligible.length === 0) return;

    setIsSending(true);

    try {
      const data = getValues();
      const res = await sendAutogiroReminder(
        eligible.map((x) => x.autogiroMandate_id),
        data.subject,
        data.body,
      );

      if (isMounted()) {
        toast.success(t('invoice:autogiroReminder.sent', { count: res.data.sent }) as string);
        onSent();
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setIsSending(false);
    }
  };

  return (
    <GenericDialog
      open={modalOpen}
      maxWidth='md'
      dialogTitle={t('invoice:autogiroReminder.title')}
      dialogContent={
        <FormProvider {...formMethods}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity={eligible.length > 0 ? 'info' : 'warning'}>
                <Typography variant='body2'>{t('invoice:autogiroReminder.eligible', { count: eligible.length, total: mandates.length })}</Typography>
                {wrongStatus.length > 0 && (
                  <Typography variant='body2'>{t('invoice:autogiroReminder.skippedWrongStatus', { count: wrongStatus.length })}</Typography>
                )}
                {missingEmail.length > 0 && (
                  <Typography variant='body2'>{t('invoice:autogiroReminder.skippedNoEmail', { count: missingEmail.length })}</Typography>
                )}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <Alert severity='info'>{t('invoice:autogiroReminder.availableFields')}</Alert>
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField name='subject' label={t('invoice:autogiroReminder.subject')} size='small' fullWidth />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextEditor name='body' rows={20} noMargin customFields={reminderCustomFields} />
            </Grid>
          </Grid>
        </FormProvider>
      }
      saveLabel='button.send'
      onSave={send}
      onClose={() => {
        if (!isSending) onClose();
      }}
    />
  );
};
